import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, ClipboardList, FileText } from "lucide-react";

export default function StaffCreate() {
  const [form, setForm] = useState({
    title: "", description: "", duration: "6 weeks", language: "English",
    class_timing: "Mon/Wed 6-7 PM IST", start_date: "2026-03-01", end_date: "2026-04-30",
    zoom_url: "", category: "General", level: "Beginner", assignment_deadline: "",
    thumbnail: "https://images.pexels.com/photos/12732215/pexels-photo-12732215.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  });
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");

  // Assignment
  const [assn, setAssn] = useState({ title: "", description: "", due_date: "", max_score: 100 });
  // Quiz
  const [quiz, setQuiz] = useState({ title: "", questions: [{ q: "", options: ["", "", "", ""], correct_index: 0 }] });
  // Material upload
  const [matTitle, setMatTitle] = useState("");
  const [matFile, setMatFile] = useState(null);
  const [matKind, setMatKind] = useState("note");
  const [matLink, setMatLink] = useState("");

  const load = async () => {
    const c = await api.get("/courses");
    setCourses(c.data);
    if (!selectedCourse && c.data[0]) setSelectedCourse(c.data[0].id);
  };
  useEffect(() => { load(); }, []);

  const createCourse = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/courses", form);
      toast.success("Course created");
      setForm({ ...form, title: "", description: "" });
      setSelectedCourse(res.data.id);
      load();
    } catch (err) { toast.error("Failed"); }
  };

  const generateZoom = async () => {
    if (!form.title) return toast.error("Enter a course title first");
    try {
      const res = await api.post("/zoom/create-meeting", {
        topic: form.title,
        start_time: `${form.start_date}T18:00:00`,
        duration_minutes: 60,
      });
      setForm((f) => ({ ...f, zoom_url: res.data.join_url }));
      toast.success("Real Zoom meeting created");
    } catch (err) {
      const msg = err.response?.data?.detail || "Zoom failed";
      if (String(msg).toLowerCase().includes("not configured")) {
        toast.info("Zoom not connected yet — paste a URL manually. Admin must set Zoom keys.");
      } else {
        toast.error(msg);
      }
    }
  };

  const delCourse = async (id) => {
    if (!confirm("Delete this course?")) return;
    await api.delete(`/courses/${id}`);
    toast.success("Deleted"); load();
  };

  const createAssn = async () => {
    if (!selectedCourse) return toast.error("Select a course");
    await api.post("/assignments", { ...assn, course_id: selectedCourse });
    toast.success("Assignment created");
    setAssn({ title: "", description: "", due_date: "", max_score: 100 });
  };

  const createQuiz = async () => {
    if (!selectedCourse) return toast.error("Select a course");
    await api.post("/quizzes", { ...quiz, course_id: selectedCourse });
    toast.success("Quiz created");
    setQuiz({ title: "", questions: [{ q: "", options: ["", "", "", ""], correct_index: 0 }] });
  };

  const uploadMat = async () => {
    if (!selectedCourse) return toast.error("Select a course");
    try {
      if (matKind === "link") {
        const fd = new FormData();
        fd.append("course_id", selectedCourse); fd.append("title", matTitle); fd.append("url", matLink);
        await api.post("/materials/link", fd);
      } else {
        if (!matFile) return toast.error("Pick a file");
        const fd = new FormData();
        fd.append("course_id", selectedCourse); fd.append("title", matTitle);
        fd.append("kind", matKind); fd.append("file", matFile);
        await api.post("/materials/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Material added");
      setMatTitle(""); setMatFile(null); setMatLink("");
    } catch (err) { toast.error("Upload failed"); }
  };

  return (
    <Layout title="Create Content" subtitle="Staff · Studio">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={createCourse} className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
          <h2 className="font-display font-bold text-lg text-slate-900">New course</h2>
          {[
            ["title", "Title"], ["description", "Description", true],
            ["duration", "Duration"], ["class_timing", "Class timing"],
            ["start_date", "Start date"], ["end_date", "End date"],
            ["assignment_deadline", "Assignment deadline"],
            ["category", "Category"],
            ["thumbnail", "Thumbnail URL"],
          ].map(([k, label, big]) => (
            <div key={k}>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
              {big ? (
                <textarea data-testid={`course-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1 w-full min-h-[80px] px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
              ) : (
                <input data-testid={`course-${k}`} required={k === "title"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
              )}
            </div>
          ))}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Zoom URL</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="course-zoom_url" value={form.zoom_url} onChange={(e) => setForm({ ...form, zoom_url: e.target.value })}
                placeholder="Paste or generate…"
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
              <button type="button" data-testid="generate-zoom-btn" onClick={generateZoom} className="px-3 py-2 rounded-xl bg-violet-600 text-white text-sm hover:bg-violet-700 whitespace-nowrap">
                Generate Zoom
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Level</label>
            <select data-testid="course-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm">
              <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
            </select>
          </div>
          <button data-testid="create-course-btn" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Create course
          </button>
        </form>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg text-slate-900 mb-2">Your courses</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {courses.map((c) => (
                <label key={c.id} className={`flex items-center gap-2 border rounded-xl px-3 py-2 cursor-pointer ${selectedCourse === c.id ? "border-blue-500 bg-blue-50" : "border-slate-100"}`}>
                  <input type="radio" name="course-select" data-testid={`select-course-${c.id}`} checked={selectedCourse === c.id} onChange={() => setSelectedCourse(c.id)} />
                  <div className="flex-1 text-sm text-slate-800 line-clamp-1">{c.title}</div>
                  <button data-testid={`del-course-${c.id}`} type="button" onClick={() => delCourse(c.id)} className="text-slate-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3"><Upload className="w-4 h-4 text-blue-600" /><h3 className="font-display font-bold text-lg text-slate-900">Add material</h3></div>
            <input value={matTitle} onChange={(e) => setMatTitle(e.target.value)} placeholder="Title" data-testid="mat-title" className="w-full px-3 py-2 rounded-xl border border-slate-200 mb-2 text-sm" />
            <select value={matKind} onChange={(e) => setMatKind(e.target.value)} data-testid="mat-kind" className="w-full px-3 py-2 rounded-xl border border-slate-200 mb-2 text-sm">
              <option value="note">Note (PDF)</option>
              <option value="video">Video</option>
              <option value="link">External link</option>
            </select>
            {matKind === "link" ? (
              <input value={matLink} onChange={(e) => setMatLink(e.target.value)} placeholder="https://…" data-testid="mat-link" className="w-full px-3 py-2 rounded-xl border border-slate-200 mb-2 text-sm" />
            ) : (
              <input type="file" onChange={(e) => setMatFile(e.target.files?.[0] || null)} data-testid="mat-file" className="w-full text-sm mb-2" />
            )}
            <button onClick={uploadMat} data-testid="mat-upload-btn" className="w-full px-3 py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700">Add</button>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3"><ClipboardList className="w-4 h-4 text-blue-600" /><h3 className="font-display font-bold text-lg text-slate-900">New assignment</h3></div>
            <input value={assn.title} onChange={(e) => setAssn({ ...assn, title: e.target.value })} placeholder="Title" data-testid="assn-title" className="w-full px-3 py-2 rounded-xl border border-slate-200 mb-2 text-sm" />
            <textarea value={assn.description} onChange={(e) => setAssn({ ...assn, description: e.target.value })} placeholder="Description" data-testid="assn-desc" className="w-full min-h-[70px] px-3 py-2 rounded-xl border border-slate-200 mb-2 text-sm" />
            <input value={assn.due_date} onChange={(e) => setAssn({ ...assn, due_date: e.target.value })} placeholder="Due date (YYYY-MM-DD)" data-testid="assn-due" className="w-full px-3 py-2 rounded-xl border border-slate-200 mb-2 text-sm" />
            <button onClick={createAssn} data-testid="create-assn-btn" className="w-full px-3 py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700">Add assignment</button>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3"><FileText className="w-4 h-4 text-violet-600" /><h3 className="font-display font-bold text-lg text-slate-900">New quiz</h3></div>
            <input value={quiz.title} onChange={(e) => setQuiz({ ...quiz, title: e.target.value })} placeholder="Quiz title" data-testid="quiz-title" className="w-full px-3 py-2 rounded-xl border border-slate-200 mb-2 text-sm" />
            {quiz.questions.map((q, i) => (
              <div key={i} className="border border-slate-100 rounded-xl p-3 mb-2 space-y-1.5">
                <input value={q.q} onChange={(e) => { const c = [...quiz.questions]; c[i].q = e.target.value; setQuiz({ ...quiz, questions: c }); }} placeholder={`Question ${i + 1}`} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input type="radio" name={`correct-${i}`} checked={q.correct_index === oi} onChange={() => { const c = [...quiz.questions]; c[i].correct_index = oi; setQuiz({ ...quiz, questions: c }); }} />
                    <input value={opt} onChange={(e) => { const c = [...quiz.questions]; c[i].options[oi] = e.target.value; setQuiz({ ...quiz, questions: c }); }} placeholder={`Option ${oi + 1}`} className="flex-1 px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
                  </div>
                ))}
              </div>
            ))}
            <button type="button" onClick={() => setQuiz({ ...quiz, questions: [...quiz.questions, { q: "", options: ["", "", "", ""], correct_index: 0 }] })} className="text-xs text-blue-600 mb-2">+ Add question</button>
            <button onClick={createQuiz} data-testid="create-quiz-btn" className="w-full px-3 py-2 rounded-full bg-violet-600 text-white text-sm hover:bg-violet-700">Publish quiz</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
